import React from "react"
import { Fade } from "react-awesome-reveal"
import moment from "moment"
import "moment/locale/cs"
import { DayPicker, Modifiers } from "react-day-picker"
import { enUS, cs } from "date-fns/locale"
import "react-day-picker/style.css"
import "styles/day-picker.scss"
import { FormattedMessage as M, useIntl, IntlShape, PrimitiveType } from "react-intl"
import axios from "axios"
import Markdown from "react-markdown"

import Firebase, {
    withFirebase,
    User,
    ApiError,
    ApiErrorCode,
    ReservationResultState,
    ReservationStatus,
    FleetReservation,
    FleetCar
} from "../Firebase"
import { UserStateCheck } from "../../shared-components/Session"
import Utilities from "../Firebase/Models-dist/utilities"

import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"

import Swal from "sweetalert2/dist/sweetalert2.js"
import withReactContent from "sweetalert2-react-content"
import { StaticDatePicker } from "components/Admin/visitorsBooking"
import muiTheme from "otherFiles/muiTheme"
import { styled } from "@mui/material"
import {
    DataGrid,
    GridColDef,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarProps,
    GridToolbarQuickFilter
} from "@mui/x-data-grid"
import { renderStatus } from "otherFiles/renderStatus"
import { csCZ as csCZGrid, enUS as enUSGrid } from "@mui/x-data-grid/locales"

const MySwal = withReactContent(Swal)

enum SelectionState {
    None = 0,
    Past,
    ReservationOpen,
    ReservationNotOpen,
    Reserved
}

const StyledDataGrid = styled(DataGrid)(() => ({
    border: 0,
    "& .MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
        marginTop: "1em",
        marginBottom: "1em"
    },
    "& .MuiDataGrid-cell": {
        paddingTop: "10px",
        paddingBottom: "10px"
    }
}))

const CustomToolbar = (_props: GridToolbarProps) => (
    <GridToolbarContainer>
        <GridToolbarQuickFilter />
        <GridToolbarExport />
        <GridToolbarColumnsButton />
    </GridToolbarContainer>
)

class ReservationData {
    topElement: JSX.Element
    firstButtonText: string
    firstButtonAction: () => void

    constructor() {
        this.topElement = <div></div>
        this.firstButtonText = ""
        this.firstButtonAction = () => {}
    }
}

interface IReserveFleetCarBaseState {
    reservations?: FleetReservation[]
    reservationGroups?: { [timestamp: number]: FleetReservation[] }
    actionInProgress: boolean
    selectedDay: Date
    selectionState: SelectionState
    selectedReservations: FleetReservation[]
    selectedDayReservations: FleetReservation[]
    fleetCars: FleetCar[]
}
interface IReserveFleetCarBaseProps {
    user: User
    firebase: Firebase
    intl: IntlShape
}
class ReserveFleetCarBase extends React.Component<IReserveFleetCarBaseProps, IReserveFleetCarBaseState> {
    //Enabled calendar period
    enabledStart = new Date()
    enabledEnd = moment().add(5, "days").endOf("isoWeek").add(4, "week").toDate()
    source = axios.CancelToken.source()
    fromDate: moment.Moment = moment.tz(this.timezone).add(-1, "M").startOf("d")

    get timezone() {
        return this.props.user.activeLocation?.timezone ?? "Europe/Prague"
    }

    endDate = moment()

    constructor(props: IReserveFleetCarBaseProps) {
        super(props)

        let selectedDay = new Date()

        this.state = {
            reservations: undefined,
            reservationGroups: undefined,
            actionInProgress: false,
            selectedDay: selectedDay,
            selectionState: SelectionState.None,
            selectedReservations: [],
            selectedDayReservations: [],
            fleetCars: []
        }

        moment.locale(this.props.intl.locale)
    }

    componentDidMount() {
        this.updateData()
    }

    async updateData() {
        if (this.props.user.fleetIds.length === 0) {
            console.log("No fleets to display")
            this.setState({ reservations: [], reservationGroups: {} })
            return
        }
        this.setState({ reservations: undefined })
        await Promise.all([this.fetchReservations(), this.fetchFleetCars()])
        this.handleDayClick(this.state.selectedDay)
    }

    setAsyncState = (newState: any) => {
        return new Promise<void>(resolve => this.setState(newState, resolve))
    }

    fetchFleetCars = async () => {
        try {
            const fleetCars = await this.props.firebase.fetchAllFleetCars(this.props.user.fleetIds[0])
            this.setState({ fleetCars })
        } catch (error) {
            console.warn("Error getting fleet cars: ", error)
        }
    }

    dayPickerMonthChange = (date: Date) => {
        this.fromDate = moment.tz(date, this.timezone).add(-15, "d").startOf("d")
        this.fetchReservations()
    }

    fetchReservations = async () => {
        try {
            const startDate = moment.tz(this.timezone).add(-1, "months").startOf("day").toDate()
            const endDate = moment.tz(this.timezone).add(1, "months").endOf("day")
            const allReservations = await this.props.firebase.fetchAllFleetReservationsWithUsers(
                this.props.user.fleetIds[0],
                startDate,
                endDate.toDate(),
                true
            )
            const reservations = allReservations.filter(r => !r.canceled)

            const reservationGroups: { [timestamp: number]: FleetReservation[] } = {}
            let date = moment.tz(startDate, this.timezone).startOf("day")
            while (date <= endDate) {
                const dateStart = moment(date).startOf("day")
                const dateEnd = moment(date).endOf("day")
                const dayReservations = reservations.filter(r => moment(r.startTime) < dateEnd && moment(r.endTime) > dateStart)
                if (dayReservations?.length > 0) {
                    reservationGroups[date.unix()] = dayReservations
                }
                date.add(1, "day")
            }

            return this.setAsyncState({ reservations, reservationGroups })
        } catch (error) {
            console.error("Error getting reservations: ", error)
            return this.setAsyncState({ reservations: [], reservationGroups: {} })
        }
    }

    fm = (messageId: string, values: Record<string, PrimitiveType> | undefined = undefined) => {
        return this.props.intl.formatMessage({ id: messageId }, values)
    }

    getAvailableCarsForSelect = async (startTime: Date, endTime: Date) => {
        const cars = await this.props.firebase.fetchAvailableCars(this.props.user.fleetIds[0], startTime, endTime)
        if (cars.length === 0) {
            throw new ApiError("There are no available cars", ApiErrorCode.NoCars)
        }
        return cars
    }

    addReservation = async () => {
        this.setState({ actionInProgress: true })

        try {
            let result: { startTime: moment.Moment; endTime: moment.Moment } | null
            result = await this.halfdayReservationFlow()

            if (result === null) {
                result = await this.minutesReservationFlow()
            }

            const availableCars = await this.getAvailableCarsForSelect(result.startTime.toDate(), result.endTime.toDate())
            const carIds = availableCars.reduce((a, car) => ({ ...a, [car.id!]: `${car.title} (${car.plateNum})` }), {})
            let selectedCar = availableCars[0]
            const carResult = await MySwal.fire({
                title: this.fm("reserve.select_car"),
                input: "select",
                inputOptions: carIds,
                html: (
                    <div id="swal-note" className="text-start">
                        <Markdown>{selectedCar.note}</Markdown>
                    </div>
                ),
                customClass: { input: "select_arrow y-style" },
                confirmButtonText: this.fm("general.next") + " &rarr;",
                didOpen: () => {
                    const input = Swal.getInput()
                    const noteDiv = document.getElementById("swal-note")
                    if (input && noteDiv) {
                        input.onchange = async () => {
                            const selectedCar = availableCars.find(c => c.id === input.value)
                            if (selectedCar && selectedCar.note) {
                                noteDiv.innerHTML = "<div>" + (await Utilities.noteToHtml(selectedCar.note)) + "</div>"
                            } else {
                                noteDiv.innerHTML = ""
                            }
                        }
                    }
                }
            })
            if (!carResult?.value) {
                throw new Error("closed")
            }
            const carId = carResult.value

            const note = await this.noteSwal()
            this.loadingSwal()

            const reservationResult = await this.props.firebase.reserveFleetCar(
                this.props.user.uid,
                this.props.user.fleetIds[0],
                result.startTime.toDate(),
                result.endTime.toDate(),
                carId,
                note
            )
            this.setState({ actionInProgress: false })

            let title = "There are no more available cars"
            let icon: "success" | "info" = "info"
            if (reservationResult.state === ReservationResultState.Confirmed) {
                title = this.fm("reserve.reservation_submitted")
                icon = "success"
            }
            MySwal.fire({
                title: title,
                html: "",
                icon: icon,
                showConfirmButton: false,
                timer: 3500,
                timerProgressBar: true
            })

            await this.fetchReservations()
            await this.handleDayClick(this.state.selectedDay)
        } catch (error: any) {
            this.setState({ actionInProgress: false })
            if (error.message === "closed") {
                return
            }
            console.log("Error: ", error)

            if (error instanceof ApiError && error.code === ApiErrorCode.NoCars) {
                return MySwal.fire({
                    title: "No cars available",
                    text: `There are no available fleet cars at this time period.`,
                    icon: "info"
                })
            }

            if (error instanceof ApiError && error.code === ApiErrorCode.ConflictReservation) {
                return MySwal.fire({
                    title: "Conflicting reservation",
                    text: `This car is already reserved.`,
                    icon: "warning"
                })
            }
            return MySwal.fire({
                title: "Error",
                text: `There was an error while submitting the reservation. ${error?.message || "Please try again later."}`,
                icon: "error"
            })
        }
    }

    cancelReservation = async (reservation: FleetReservation) => {
        MySwal.showLoading()
        this.setState({ actionInProgress: true })
        try {
            await this.props.firebase.cancelFleetReservation(this.props.user.fleetIds[0], reservation.id)

            this.setState({ actionInProgress: false })
            MySwal.fire({
                title: this.fm("reserve.cancel_success"),
                icon: "success",
                showConfirmButton: false,
                timer: 1500,
                timerProgressBar: true
            })
            await this.fetchReservations()
            this.handleDayClick(this.state.selectedDay)
        } catch (error) {
            this.setState({ actionInProgress: false })
            MySwal.fire({
                title: "Error",
                text: "There was an error while canceling the reservation. Please try again later.",
                icon: "error"
            })
            console.warn("Error canceling reservation: ", error)
        }
    }

    /*
    recordOdometer = async (reservation: FleetReservation, fleetCar?: FleetCar) => {
        try {
            const odometerInitial = await this.odometerSwal(
                this.fm("fleet_reservation.odometer_initial"),
                this.fm("fleet_reservation.odometer_initial_text"),
                reservation.odometerInitial ?? fleetCar?.odometer
            )
            const odometerFinal = await this.odometerSwal(
                this.fm("fleet_reservation.odometer_final"),
                this.fm("fleet_reservation.odometer_final_text"),
                reservation.odometerFinal
            )
            const note = await this.noteSwal(reservation.note)
            this.loadingSwal()

            let reservationEndTime: Date | undefined = undefined
            if (odometerFinal && moment(reservation.endTime).isAfter()) {
                reservationEndTime = new Date()
            }

            await this.props.firebase.updateFleetReservation(this.props.user.fleetIds[0], reservation.id, {
                odometerInitial,
                odometerFinal,
                note,
                endTime: reservationEndTime
            })

            if (fleetCar && odometerFinal) {
                await this.props.firebase.updateFleetCar(this.props.user.fleetIds[0], fleetCar.id, {
                    odometer: odometerFinal
                })
            }

            await this.fetchFleetCars()
            await this.fetchReservations()
            this.handleDayClick(this.state.selectedDay)

            MySwal.fire({
                title: this.fm("general.save_success"),
                icon: "success",
                showConfirmButton: false,
                timer: 1500,
                timerProgressBar: true
            })
        } catch (error: unknown) {
            if (error instanceof Error) {
                if (error.message === "closed") {
                    return
                }
            }
            console.log("Unknown error occurred: ", error)
            MySwal.fire({
                title: "Error",
                text: "There was an error. Please try again later.",
                icon: "error"
            })
        }
    }
    */

    endReservation = async (reservation: FleetReservation) => {
        try {
            const note = await this.noteSwal(reservation.note)
            this.loadingSwal()

            let reservationEndTime: Date | undefined = undefined
            if (moment(reservation.endTime).isAfter()) {
                reservationEndTime = new Date()
            }

            await this.props.firebase.updateFleetReservation(this.props.user.fleetIds[0], reservation.id, {
                note,
                endTime: reservationEndTime
            })

            await this.fetchFleetCars()
            await this.fetchReservations()
            this.handleDayClick(this.state.selectedDay)

            MySwal.fire({
                title: this.fm("general.save_success"),
                icon: "success",
                showConfirmButton: false,
                timer: 1500,
                timerProgressBar: true
            })
        } catch (error: unknown) {
            if (error instanceof Error) {
                if (error.message === "closed") {
                    return
                }
            }
            console.log("Unknown error occurred: ", error)
            MySwal.fire({
                title: "Error",
                text: "There was an error. Please try again later.",
                icon: "error"
            })
        }
    }

    renderDay = (date: Date, modifiers: Modifiers) => {
        return <span>{date.getDate()}</span>
    }

    handleDayClick = (day: Date): void => {
        const dateStart = moment(day).startOf("day")
        const dateEnd = moment(day).endOf("day")
        const allDayReservations = (this.state.reservations ?? []).filter(r => moment(r.startTime) < dateEnd && moment(r.endTime) > dateStart)

        const selectedReservations = allDayReservations.filter(r => r.user?.uid === this.props.user.uid)

        let selectionState
        if (selectedReservations.some(r => r.canceled === false)) {
            selectionState = SelectionState.Reserved
        } else {
            const today = moment.tz(this.timezone).startOf("day").toDate()

            if (day < today) {
                selectionState = SelectionState.Past
            } else if (day <= this.enabledEnd) {
                selectionState = SelectionState.ReservationOpen
            } else {
                selectionState = SelectionState.ReservationNotOpen
            }
        }

        this.setState({
            selectedDay: day,
            selectionState: selectionState,
            selectedReservations: selectedReservations,
            selectedDayReservations: allDayReservations
        })
    }

    onSelectChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (event.target.name === "fleet") {
            this.setState({ reservations: undefined })
            await this.props.firebase.setUserActiveFleet(this.props.user, event.target.value)
            this.updateData()
        }
    }

    render() {
        if (this.state.reservations === undefined || this.state.reservationGroups === undefined) {
            return <div className="loader"></div>
        }

        const modifiers = {
            confirmed: Object.entries(this.state.reservationGroups)
                .filter(([_, reservations]) => reservations.some(r => !r.canceled && r.user?.uid === this.props.user.uid))
                .map(([timestamp]) => moment.unix(Number(timestamp)).toDate()),
            outsideRange: { before: this.enabledStart, after: this.enabledEnd }
        }
        const modifiersClassNames = {
            pending: "rdp-day--pending",
            confirmed: "rdp-day--confirmed",
            declined: "rdp-day--declined",
            outsideRange: "rdp-day--outside-range",
            today: "rdp-day--today"
        }

        let reservationsData: ReservationData[] = []
        switch (this.state.selectionState) {
            case SelectionState.Reserved: {
                for (const reservation of this.state.selectedReservations) {
                    const reservationData = new ReservationData()
                    const date = Utilities.reservationTimeToText(reservation, this.timezone, this.props.intl.locale)
                    const fleetCar = this.state.fleetCars.find(car => car.id === reservation.fleetCarId)
                    reservationData.topElement = (
                        <div className="text-start">
                            <div>
                                <strong>{fleetCar?.title ?? "Unknown car"}</strong> ({fleetCar?.plateNum ?? "-"})
                            </div>
                            <div>
                                <Markdown>{fleetCar?.note}</Markdown>
                            </div>
                            {/*
                            <div>
                                <M id="fleet_reservation.odometer" />: {fleetCar?.odometer ?? "-"} km
                            </div>
                            */}
                            <div>
                                <strong>{date}</strong>
                            </div>
                            {reservation.note ? (
                                <>
                                    <div>
                                        <Markdown>{reservation.note}</Markdown>
                                    </div>
                                </>
                            ) : (
                                <></>
                            )}

                            {/*reservation.odometerInitial && reservation.odometerFinal ? (
                                <>
                                    <div>
                                        {reservation.odometerInitial} km - {reservation.odometerFinal} km (
                                        {reservation.odometerFinal - reservation.odometerInitial} km)
                                    </div>
                                </>
                            ) : (
                                <></>
                            )*/}
                        </div>
                    )

                    if (!reservation.endTime || moment(reservation.endTime).isSameOrAfter(moment())) {
                        if (reservation.startTime < new Date()) {
                            reservationData.firstButtonText = this.fm("fleet_reservation.end")
                            reservationData.firstButtonAction = () => this.endReservation(reservation)
                        } else {
                            reservationData.firstButtonText = this.fm("reserve.cancel_reservation")
                            reservationData.firstButtonAction = () => this.cancelReservation(reservation)
                        }
                    }
                    reservationsData.push(reservationData)
                }
            }
        }

        const reservationControlBlock = reservationsData.map((data, i) => (
            <Row className="my-3 reservations-panel" key={i}>
                <Fade className="w-100 text-center">{data.topElement}</Fade>
                <div className="mt-3 mx-auto">
                    <button
                        type="submit"
                        className="btn btn-primary hm-btn m-2"
                        hidden={!data.firstButtonText}
                        onClick={data.firstButtonAction}
                        disabled={this.state.actionInProgress}
                    >
                        {data.firstButtonText}
                    </button>
                    <hr />
                </div>
            </Row>
        ))

        const reserveCarButton = (
            <button
                type="button"
                className="btn btn-primary hm-btn m-2"
                hidden={this.state.selectedDay < moment.tz(this.timezone).startOf("day").toDate()}
                onClick={this.addReservation}
                disabled={this.state.actionInProgress}
            >
                {this.fm("reserve.reserve")}
            </button>
        )

        this.state.selectedReservations?.sort((a, b) => {
            return a.user?.surname.localeCompare(b.user?.surname ?? "") ?? 0
        })

        const columns: GridColDef[] = [
            { field: "firstName", headerName: this.fm("fleet_reservation.first_name"), width: 150 },
            { field: "lastName", headerName: this.fm("fleet_reservation.last_name"), width: 180 },
            { field: "carTitle", headerName: this.fm("fleet_reservation.car"), width: 150 },
            { field: "licensePlate", headerName: this.fm("fleet_reservation.license_plate"), width: 180 },
            { field: "status", headerName: this.fm("fleet_reservation.status"), width: 150, renderCell: renderStatus },
            { field: "time", headerName: this.fm("fleet_reservation.reservation_time"), width: 200 },
            //{ field: "odometerInitial", headerName: "Odometer - initial", width: 150 },
            //{ field: "odometerFinal", headerName: "Odometer - final", width: 150 },
            {
                field: "note",
                headerName: this.fm("reserve.enter_note"),
                width: 300,
                renderCell: params => (
                    <div>
                        <Markdown>{params.row.note}</Markdown>
                    </div>
                )
            },
            {
                field: "dateCreated",
                headerName: this.fm("fleet_reservation.created_at"),
                width: 150,
                valueFormatter: ({ value }) =>
                    value ? `${moment.tz(value, this.timezone).format("l")} ${moment.tz(value, this.timezone).format("LT")}` : undefined
            }
        ]

        const rows =
            this.state.selectedDayReservations?.map(r => {
                const startTime = moment.tz(r.startTime, this.timezone)
                const endTime = moment.tz(r.endTime, this.timezone)
                let timeText = ""
                if (startTime.isSame(endTime, "day")) {
                    timeText = Utilities.reservationTimeToTextMedium(r, this.timezone, "en")
                } else {
                    timeText = Utilities.reservationTimeToText(r, this.timezone, "en")
                }

                const car = this.state.fleetCars?.find(c => c.id === r.fleetCarId)

                return {
                    id: r.id,
                    firstName: r.user?.name,
                    lastName: r.user?.surname,
                    carTitle: car?.title,
                    licensePlate: car?.plateNum,
                    status: r.canceled ? "Canceled" : ReservationStatus.Confirmed,
                    time: timeText,
                    dateCreated: r.timestampCreated?.getTime(),
                    //odometerInitial: r.odometerInitial,
                    //odometerFinal: r.odometerFinal,
                    note: r.note
                }
            }) ?? []

        let occupancyText = <></>

        return (
            <div>
                {(this.props.user.fleets ?? []).length > 1 ? (
                    <div>
                        <select
                            className="mb-3 select_arrow y-style"
                            name="fleet"
                            id="fleet"
                            value={this.props.user.fleets![0].id}
                            onChange={this.onSelectChange}
                        >
                            {this.props.user.fleets?.map(fleet => (
                                <option key={fleet.id} value={fleet.id}>
                                    {fleet.title}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : (
                    <div></div>
                )}
                <Row className="m-0">
                    <Col className="text-center">
                        <DayPicker
                            onDayClick={this.handleDayClick}
                            selected={this.state.selectedDay}
                            showOutsideDays
                            modifiers={modifiers}
                            modifiersClassNames={modifiersClassNames}
                            onMonthChange={this.dayPickerMonthChange}
                            locale={this.props.intl.locale === "cs" ? cs : enUS}
                        />
                    </Col>
                    <Col className="d-flex align-items-start flex-column justify-content-center">
                        {reservationControlBlock}
                        {reserveCarButton}
                    </Col>
                </Row>
                {this.state.selectionState !== SelectionState.None ? (
                    <div className="mt-md-5">
                        <h5>
                            {this.fm("fleet_reservation.for_day")} {moment(this.state.selectedDay).format("LL")}:
                        </h5>
                        {/*<h5>Reservations for {moment(this.state.selectedDay!).format('LL')}:</h5>*/}
                        <div>{occupancyText}</div>
                        <StyledDataGrid
                            rows={rows}
                            columns={columns}
                            initialState={{
                                pagination: {
                                    paginationModel: { page: 0, pageSize: 20 }
                                },
                                columns: {
                                    columnVisibilityModel: {
                                        priority: false,
                                        dateCreated: false
                                    }
                                }
                            }}
                            pageSizeOptions={[10, 20, 50]}
                            slots={{ toolbar: CustomToolbar }}
                            disableRowSelectionOnClick
                            getRowHeight={() => "auto"}
                            autoHeight
                            localeText={
                                this.props.intl.locale === "cs"
                                    ? csCZGrid.components.MuiDataGrid.defaultProps.localeText
                                    : enUSGrid.components.MuiDataGrid.defaultProps.localeText
                            }
                        />
                    </div>
                ) : (
                    <></>
                )}
            </div>
        )
    }

    halfdayReservationFlow = () => {
        const select = (
            <select className="mb-1 select_arrow y-style" name="period" id="period" defaultValue="day">
                <option key={"am"} value={"am"}>
                    {this.fm("reserve.morning")}
                </option>
                <option key={"pm"} value={"pm"}>
                    {this.fm("reserve.afternoon")}
                </option>
                <option key={"day"} value={"day"}>
                    {this.fm("reserve.whole_day")}
                </option>
                <option key={"other"} value={"other"}>
                    {this.fm("reserve.other")}
                </option>
            </select>
        )
        return MySwal.fire({
            title: this.fm("reserve.reservation_time"),
            html: select,
            confirmButtonText: this.fm("general.next") + " &rarr;",
            preConfirm: () => {
                return (document!.getElementById("period") as HTMLInputElement).value
            }
        }).then(result => {
            if (result.isDismissed) {
                //dismissed dialog
                return Promise.reject(new Error("closed"))
            }
            let startTime = moment.tz(this.state.selectedDay, this.timezone).startOf("d")
            let endTime = moment.tz(this.state.selectedDay, this.timezone).endOf("d")
            if (result.value === "am") {
                endTime = moment.tz(this.state.selectedDay, this.timezone).set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
            } else if (result.value === "pm") {
                startTime = moment.tz(this.state.selectedDay, this.timezone).set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
            } else if (result.value === "other") {
                return null
            }
            return { startTime, endTime }
        })
    }

    onEndDateChange = (date: string | moment.Moment | null) => {
        if (date === null) {
            return
        }
        const endDate = this.onDateChange(date)
        if (endDate) {
            this.endDate = endDate
        }
    }
    onDateChange = (date: moment.Moment | string) => {
        const filterDate = moment.isMoment(date) ? date : moment(date)
        if (filterDate.isValid()) {
            return filterDate
        } else {
            return null
        }
    }

    minutesReservationFlow = async () => {
        const startTime = await this.timeSwal(this.fm("reserve.start_time"), "08:00", this.state.selectedDay)

        const endDateResult = await MySwal.fire({
            title: "Select reservation end date",
            html: (
                <StaticDatePicker
                    date={moment(this.state.selectedDay)}
                    onChange={this.onEndDateChange}
                    timezone={this.timezone}
                    locale={this.props.intl.locale}
                    theme={muiTheme}
                />
            ),
            confirmButtonText: this.fm("general.next") + " &rarr;"
        })
        if (!endDateResult.value) {
            throw new Error("closed")
        }

        const endTime = await this.timeSwal(this.fm("reserve.end_time"), "16:00", this.endDate.toDate())

        return { startTime, endTime }
    }

    loadingSwal = () => {
        MySwal.fire({
            didOpen: () => {
                Swal.showLoading()
            }
        })
    }

    noteSwal = async (defaultValue: string = ""): Promise<string> => {
        const result = await MySwal.fire({
            title: this.fm("reserve.enter_note"),
            input: "textarea",
            inputValue: defaultValue,
            customClass: { input: "y-style" },
            confirmButtonText: this.fm("general.next") + " &rarr;"
        })
        if (result.value === undefined) {
            throw new Error("closed")
        }
        return result.value as string
    }

    /*
    odometerSwal = async (title: string, text: string, defaultValue?: number): Promise<number | undefined> => {
        const result = await MySwal.fire({
            title: title,
            text: text,
            input: "number",
            inputValue: defaultValue ?? "",
            customClass: { input: "y-style" },
            confirmButtonText: this.fm("general.next") + " &rarr;",
            cancelButtonText: this.fm("general.skip"),
            showCancelButton: defaultValue === undefined
        })
        if (result.value) {
            return parseInt(result.value)
        }
        if (result.isConfirmed || result.dismiss === Swal.DismissReason.cancel) {
            return undefined
        }
        throw new Error("closed")
    }
    */

    timeSwal = async (title: string, defaultValue: string = "08:00", baseDate: Date): Promise<moment.Moment> => {
        const result = await MySwal.fire({
            title: title,
            html: <input id="time-input" className="y-style" defaultValue={defaultValue} type="time" />,
            confirmButtonText: this.fm("general.next") + " &rarr;",
            preConfirm: () => {
                return (document!.getElementById("time-input") as HTMLInputElement).value
            }
        })
        if (result.isDismissed) {
            return Promise.reject(new Error("closed"))
        }
        return Utilities.parseTime(result.value!, baseDate, this.timezone)
    }
}

const ReserveSpot = (props: any) => {
    const intl = useIntl()
    return (
        <div className="parkfair-container">
            <h3>
                <M id="nav.fleet_reservation" />
            </h3>
            <hr className="d-none d-md-block" />
            <UserStateCheck>
                {userInfo => (
                    <Fade>
                        <ReserveFleetCarBase user={userInfo.user} {...props} intl={intl} />
                    </Fade>
                )}
            </UserStateCheck>
        </div>
    )
}

export default withFirebase(ReserveSpot)
